import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { clamp, drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const ROUND_SECONDS = 60;

type RouteKind = "clean" | "degraded";

interface RouteSignal {
  lane: number;
  depth: number;
  kind: RouteKind;
}

export class LatencyLeagueGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0x1a7e);
  private readonly signals: RouteSignal[] = [];
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private distance = 0;
  private playerPosition = 0;
  private playerVelocity = 0;
  private spawnTimer = 0.55;
  private boostEnergy = 1;
  private boosting = false;
  private integrity = 3;
  private shake = 0;
  private message = "Read the route and hold the clean line";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {}

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.shake = Math.max(0, this.shake - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Read the route and hold the clean line";

    if (this.time === 0) {
      this.finish("Clean route completed");
      return;
    }

    this.updateSteering(deltaSeconds);
    this.updateRoute(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    context.save();
    if (this.shake > 0 && !this.services.reducedMotion) {
      context.translate(Math.sin(this.elapsed * 92) * 7, Math.cos(this.elapsed * 73) * 4);
    }
    this.drawWorld(context);
    const signals = [...this.signals].sort((left, right) => right.depth - left.depth);
    for (const signal of signals) this.drawSignal(context, signal);
    this.drawVehicle(context);
    context.restore();
    this.drawTelemetry(context);
    this.drawFooter(context);
    drawScanlines(context, "rgba(255, 255, 255, 0.018)");
  }

  getHud(): GameHud {
    return {
      score: Math.floor(this.score),
      time: this.time,
      message: this.message,
      ended: this.ended,
    };
  }

  destroy(): void {}

  private updateSteering(deltaSeconds: number): void {
    const input = this.services.input;
    const direction = Number(input.isDown("right")) - Number(input.isDown("left"));
    const targetVelocity = direction * 1.65;
    this.playerVelocity += (targetVelocity - this.playerVelocity) * Math.min(1, deltaSeconds * 9);
    if (direction === 0) this.playerVelocity *= Math.max(0, 1 - deltaSeconds * 5);
    this.playerPosition = clamp(this.playerPosition + this.playerVelocity * deltaSeconds, -1, 1);

    this.boosting = input.isDown("primary") && this.boostEnergy > 0.02;
    if (this.boosting) {
      this.boostEnergy = Math.max(0, this.boostEnergy - deltaSeconds * 0.34);
    } else {
      this.boostEnergy = Math.min(1, this.boostEnergy + deltaSeconds * 0.12);
    }
  }

  private updateRoute(deltaSeconds: number): void {
    const speed = this.boosting ? 0.52 : 0.36;
    this.distance += speed * deltaSeconds;
    this.score += speed * deltaSeconds * 92;
    this.spawnTimer -= deltaSeconds;

    if (this.spawnTimer <= 0 && this.signals.length < 10) {
      this.spawnSignal();
      this.spawnTimer = this.random.between(0.58, 0.88);
    }

    for (let index = this.signals.length - 1; index >= 0; index -= 1) {
      const signal = this.signals[index];
      if (!signal) continue;
      signal.depth -= speed * deltaSeconds;
      if (signal.depth > 0.05) continue;
      this.resolveSignal(signal);
      this.signals.splice(index, 1);
      if (this.ended) return;
    }
  }

  private spawnSignal(): void {
    const lanes = [-0.78, -0.26, 0.26, 0.78] as const;
    const lane = lanes[this.random.integer(lanes.length)] ?? 0;
    this.signals.push({
      lane,
      depth: 1.05,
      kind: this.random.next() < 0.58 ? "clean" : "degraded",
    });
  }

  private resolveSignal(signal: RouteSignal): void {
    const collision = Math.abs(signal.lane - this.playerPosition) < 0.23;
    if (signal.kind === "clean" && collision) {
      const boostBonus = this.boosting ? 55 : 0;
      this.score += 140 + boostBonus;
      this.services.audio.play("hit");
      this.setMessage(this.boosting ? "Direct route captured - boost bonus" : "Clean route captured", 1.5);
      return;
    }

    if (signal.kind === "degraded" && collision) {
      this.integrity -= 1;
      this.score = Math.max(0, this.score - 90);
      this.shake = 0.38;
      this.services.audio.play("miss");
      this.setMessage(`Degraded lane hit - ${this.integrity} integrity remaining`, 1.8);
      if (this.integrity <= 0) this.finish("Route quality collapsed");
      return;
    }

    if (signal.kind === "degraded") this.score += 25;
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${Math.floor(this.score)}.`);
  }

  private drawWorld(context: CanvasRenderingContext2D): void {
    const sky = context.createLinearGradient(0, 0, 0, 720);
    sky.addColorStop(0, "#071a31");
    sky.addColorStop(0.55, "#0c3150");
    sky.addColorStop(1, "#07101e");
    context.fillStyle = sky;
    context.fillRect(-20, -20, 1320, 760);

    context.fillStyle = "rgba(47, 227, 255, 0.08)";
    context.beginPath();
    context.arc(250, 176, 138, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = "#0b1525";
    context.beginPath();
    context.moveTo(-20, 420);
    context.lineTo(245, 240);
    context.lineTo(425, 400);
    context.lineTo(650, 180);
    context.lineTo(860, 414);
    context.lineTo(1035, 255);
    context.lineTo(1300, 420);
    context.lineTo(1300, 740);
    context.lineTo(-20, 740);
    context.closePath();
    context.fill();

    const road = context.createLinearGradient(0, 260, 0, 720);
    road.addColorStop(0, "#111827");
    road.addColorStop(1, "#050914");
    context.fillStyle = road;
    context.beginPath();
    context.moveTo(565, 250);
    context.lineTo(715, 250);
    context.lineTo(1160, 720);
    context.lineTo(120, 720);
    context.closePath();
    context.fill();

    context.strokeStyle = "#2fe3ff";
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(565, 250);
    context.lineTo(120, 720);
    context.moveTo(715, 250);
    context.lineTo(1160, 720);
    context.stroke();

    for (const lane of [-0.5, 0, 0.5]) {
      context.strokeStyle = "rgba(168, 242, 255, 0.36)";
      context.lineWidth = 2;
      for (let segment = 0; segment < 8; segment += 1) {
        const startProgress = ((segment / 8 + this.distance * 1.8) % 1) * 0.94;
        const endProgress = Math.min(1, startProgress + 0.055 + startProgress * 0.045);
        const start = this.roadPoint(lane, 1 - startProgress);
        const end = this.roadPoint(lane, 1 - endProgress);
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
      }
    }

    if (this.boosting && !this.services.reducedMotion) {
      context.strokeStyle = "rgba(47, 227, 255, 0.5)";
      context.lineWidth = 3;
      for (let index = 0; index < 18; index += 1) {
        const x = (index * 83 + this.elapsed * 910) % 1280;
        const y = 270 + ((index * 59 + this.elapsed * 520) % 390);
        context.beginPath();
        context.moveTo(x, y);
        context.lineTo(x - 34, y + 24);
        context.stroke();
      }
    }
  }

  private roadPoint(lane: number, depth: number): { x: number; y: number; scale: number } {
    const progress = 1 - clamp(depth, 0, 1);
    const curvedProgress = progress * progress;
    const halfWidth = 54 + curvedProgress * 465;
    return {
      x: 640 + lane * halfWidth,
      y: 250 + curvedProgress * 470,
      scale: 0.2 + curvedProgress * 0.95,
    };
  }

  private drawSignal(context: CanvasRenderingContext2D, signal: RouteSignal): void {
    const point = this.roadPoint(signal.lane, signal.depth);
    const size = 62 * point.scale;
    const clean = signal.kind === "clean";
    const accent = clean ? "#2fe3ff" : "#e75bc4";
    context.save();
    context.translate(point.x, point.y);
    context.shadowColor = accent;
    context.shadowBlur = 18 * point.scale;
    context.fillStyle = "rgba(5, 11, 20, 0.9)";
    context.strokeStyle = accent;
    context.lineWidth = Math.max(2, 4 * point.scale);

    if (clean) {
      roundedRectPath(context, -size * 0.75, -size * 0.5, size * 1.5, size, size * 0.2);
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      context.strokeStyle = "#f7fbff";
      context.beginPath();
      context.moveTo(-size * 0.35, 0);
      context.lineTo(size * 0.35, 0);
      context.lineTo(size * 0.1, -size * 0.2);
      context.moveTo(size * 0.35, 0);
      context.lineTo(size * 0.1, size * 0.2);
      context.stroke();
    } else {
      context.beginPath();
      for (let side = 0; side < 8; side += 1) {
        const angle = Math.PI / 8 + side * (Math.PI / 4);
        const x = Math.cos(angle) * size * 0.58;
        const y = Math.sin(angle) * size * 0.58;
        if (side === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.closePath();
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      context.strokeStyle = "#f7fbff";
      context.beginPath();
      context.moveTo(-size * 0.25, -size * 0.25);
      context.lineTo(size * 0.25, size * 0.25);
      context.moveTo(size * 0.25, -size * 0.25);
      context.lineTo(-size * 0.25, size * 0.25);
      context.stroke();
    }

    if (point.scale > 0.45) {
      setText(context, Math.max(9, 11 * point.scale), 900, accent, "center");
      context.fillText(clean ? "DIRECT" : "DEGRADED", 0, size * 0.78);
    }
    context.restore();
  }

  private drawVehicle(context: CanvasRenderingContext2D): void {
    const x = 640 + this.playerPosition * 430;
    const tilt = this.playerVelocity * 0.12;
    context.save();
    context.translate(x, 625);
    context.rotate(tilt);
    context.shadowColor = "#2fe3ff";
    context.shadowBlur = this.boosting ? 34 : 17;
    context.fillStyle = "#0b2238";
    context.strokeStyle = "#2fe3ff";
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(0, -46);
    context.lineTo(55, 33);
    context.lineTo(25, 27);
    context.lineTo(0, 44);
    context.lineTo(-25, 27);
    context.lineTo(-55, 33);
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.fillStyle = "#ff7a1a";
    context.fillRect(-12, 16, 24, this.boosting ? 54 : 28);
    context.fillStyle = "#f7fbff";
    context.beginPath();
    context.arc(0, -4, 11, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }

  private drawTelemetry(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 42, 330, 128, "#ff7a1a");
    setText(context, 12, 850, "#a8f2ff");
    context.fillText("ROUTE TELEMETRY", 72, 72);
    setText(context, 25, 900, "#f7fbff");
    context.fillText(this.boosting ? "EDGE BOOST" : "DIRECT ROUTE", 72, 106);
    setText(context, 11, 850, "#839aa5");
    context.fillText("BOOST", 72, 143);
    context.fillStyle = "rgba(255, 255, 255, 0.12)";
    roundedRectPath(context, 125, 137, 205, 12, 6);
    context.fill();
    context.fillStyle = this.boosting ? "#ff7a1a" : "#2fe3ff";
    roundedRectPath(context, 125, 137, Math.max(3, 205 * this.boostEnergy), 12, 6);
    context.fill();

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("ROUTE INTEGRITY", 1232, 62);
    for (let index = 0; index < 3; index += 1) {
      const x = 1126 + index * 42;
      context.strokeStyle = index < this.integrity ? "#2fe3ff" : "#485362";
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(x, 83);
      context.lineTo(x + 15, 90);
      context.lineTo(x + 12, 110);
      context.lineTo(x, 118);
      context.lineTo(x - 12, 110);
      context.lineTo(x - 15, 90);
      context.closePath();
      context.stroke();
    }
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 650, 1196, 54, "#ff7a1a");
    setText(context, 20, 850, "#f7fbff");
    context.fillText(this.message, 72, 678);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("STEER  ← →     BOOST  SPACE", 1204, 678);
  }
}
